import { em, getBreakpointValue, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { rightSidebarStore } from '../components/RightSidebar/store';

/**
 * Breakpoint calculations for sidebar behavior.
 *
 * There are three different breakpoints used for different purposes:
 * 1. Mobile breakpoint (md - 1): Used for CSS media queries and portal behavior
 * 2. Auto-collapse breakpoint (md + 24): Used for window resize logic to prevent conflicts
 *
 * The +24 buffer ensures the sidebar auto-collapses slightly before the mobile breakpoint
 * kicks in, preventing layout conflicts between auto-collapse and mobile overlay behavior.
 */

/**
 * Hook that provides all sidebar-related breakpoint calculations.
 *
 * @returns Object containing breakpoint values and derived calculations
 */
export function useSidebarBreakpoints() {
	const theme = useMantineTheme();
	const mdBreakpoint = getBreakpointValue(theme.breakpoints.md);

	return {
		// Base breakpoint values
		mdBreakpoint,

		// Mobile breakpoint (md - 1): Used for CSS media queries and portal behavior
		mobileBreakpoint: mdBreakpoint - 1,
		mobileBreakpointQuery: `(max-width: ${em(mdBreakpoint - 1)})`,

		// Auto-collapse breakpoint (md + 24): Used for window resize logic
		autoCollapseBreakpoint: mdBreakpoint + 24,

		// Derived values
		isMobile: useMediaQuery(`(max-width: ${em(mdBreakpoint - 1)})`),
		isSidebarOpen: !rightSidebarStore.collapsed,
	};
}

/**
 * Hook to determine if components should use portal mode based on mobile breakpoint and sidebar state.
 *
 * This hook calculates whether floating components (like dropdowns, modals, etc.) should render
 * within a portal to avoid being clipped by the mobile sidebar overlay.
 *
 * @returns shouldUsePortal - true if components should render in portal, false otherwise
 */
export function useRightSidebarPortalBehavior(): boolean {
	const { isMobile, isSidebarOpen } = useSidebarBreakpoints();

	// Use portal when NOT on mobile OR when sidebar is closed
	// On mobile with open sidebar, don't use portal to avoid clipping
	return !(isMobile && isSidebarOpen);
}
